import { useCart } from "../providers/cart.provider";
import { useEffect, useMemo, useState } from "react";
import { useAddress } from "../components/addressManagement/addressManagement";
import { useNavigate } from "react-router";
import { Mixpanel } from "../utils/mixpanel";
import { activateNotification } from "../utils/notifications-utils";

export const useUserCart = () => {
  const {
    cartItems,
    removeFromCart: handleDelete,
    handleCheckout,
    loadingCart: loading,
    updateCartItemQuantity,
    actionLoading,
  } = useCart();
  const [selectedIds, setSelectedIds] = useState([]);
  const [quantities, setQuantities] = useState({});

  const { isAddressModalOpen, setIsAddressModalOpen, toggleAddressModal } =
    useAddress();
  const navigate = useNavigate();

  const normalizedItems = useMemo(() => {
    return cartItems.map((item) => {
      const cartItemId = item._id;
      const splitBuyId = item.split_buy_id;

      return {
        cart_item_id: item._id,
        split_buy_id: splitBuyId,
        split_buy_slug: item.split_buy_slug,
        image: item?.image || "",
        title: item?.title || "Unknown Product",
        location: item?.location || "Location not specified",
        delivery_date: item?.delivery_date,
        price: item?.price,
        available_slots: item?.available_slots,
        total_slots:
          item?.total_slots ||
          Number(item?.available_slots) +
            Number(item?.number_of_buy_ins_attained),
        quantity: quantities[cartItemId] || item.slot || 1,
        join_period_end_date: item?.join_period_end_date,
        number_of_buy_ins_attained: item?.number_of_buy_ins_attained,
        selected: selectedIds.includes(splitBuyId),
        raw: item,
      };
    });
  }, [cartItems, selectedIds, quantities]);

  // Select All functionality
  const allSplitBuyIds = useMemo(() => {
    return cartItems.map((item) => item.split_buy_id);
  }, [cartItems]);

  const isAllSelected = useMemo(() => {
    return cartItems.length > 0 && selectedIds.length === allSplitBuyIds.length;
  }, [selectedIds, allSplitBuyIds, cartItems.length]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
      Mixpanel.alias("Tap-CartScreen-UnSelectAllCheckbox");
    } else {
      setSelectedIds([...allSplitBuyIds]);
      Mixpanel.alias("Tap-CartScreen-SelectAllCheckbox");
    }
  };

  const toggleSelect = (cartItemId) => {
    const wasSelected = selectedIds.includes(cartItemId);
    setSelectedIds((prev) =>
      wasSelected
        ? prev.filter((id) => id !== cartItemId)
        : [...prev, cartItemId]
    );
    if (wasSelected) {
      Mixpanel.track("Tap-CartScreen-UnselectSplitBuyCheckbox", {
        split_buy_id: cartItemId,
      });
    } else {
      Mixpanel.track("Tap-CartScreen-SelectSplitBuyCheckbox", {
        split_buy_id: cartItemId,
      });
    }
  };

  const updateQuantity = async (cartItemId, newQuantity) => {
    setQuantities((prev) => ({
      ...prev,
      [cartItemId]: newQuantity,
    }));
    await updateCartItemQuantity(cartItemId, newQuantity);
  };

  const handleRemove = async (cartItemId) => {
    await handleDelete(cartItemId);
    setSelectedIds((prev) => prev.filter((id) => id !== cartItemId));
    setQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[cartItemId];
      return newQuantities;
    });
  };
  const handleCartItemClick = (item) => {
    if (item.split_buy_slug) {
      navigate(`/split-buy/details/${item.split_buy_slug}`, {
        state: {
          fromInternal: true,
          fromPage: "cart",
          cartItemData: item,
        },
      });
    }
  };

  const { subTotal, total, selectedItems } = useMemo(() => {
    const selectedItems = normalizedItems.filter((item) => item.selected);
    const subTotal = selectedItems.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );
    const total = subTotal;
    return { subTotal, total, selectedItems };
  }, [normalizedItems]);

  useEffect(() => {
    if (cartItems.length > 0) {
      const initialQuantities = {};
      cartItems.forEach((item) => {
        initialQuantities[item._id] = item.slot || item.quantity || 1;
      });
      setQuantities(initialQuantities);
    }
  }, [cartItems]);

  useEffect(() => {
    if (cartItems.length > 0) {
      const allSplitBuyIds = cartItems.map((item) => item.split_buy_id);
      setSelectedIds(allSplitBuyIds);
    }
  }, [cartItems]);

  useEffect(() => {
    Mixpanel.track_view_cart_screen(cartItems?.length || 0);
  }, []);

  const onCheckout = async () => {
    try {
      if (selectedIds.length === 0 || actionLoading) return;

      Mixpanel.track("Tap-CartScreen-ProceedToCheckoutButton", {
        selected_items_count: selectedItems,
      });

      const splitBuyToCartItemMap = {};
      cartItems.forEach((item) => {
        splitBuyToCartItemMap[item.split_buy_id] = item._id;
      });

      const split_buy = selectedIds.map((splitBuyId) => {
        const cartItemId = splitBuyToCartItemMap[splitBuyId];
        // const cartItem = cartItems.find(item => item._id === cartItemId);

        return {
          id: splitBuyId,
          number_of_slots_to_be_purchased: quantities[cartItemId] || 1,
          // state: cartItem?.split_buy?.split_buy_product_location?.string ||
          //     cartItem?.split_buy?.choice_of_delivery_locations?.[0] ||
          //     cartItem?.location ||
          //     null
        };
      });

      const {
        success,
        message,
        data: respData,
      } = await handleCheckout(split_buy);
      if (message !== "Authentication required" && !success) {
        activateNotification({
          title: "Error",
          body: message,
        });
      }

      if (success) return navigate("/cart/checkout", { state: respData?.data });

      if (message?.includes("Please add a delivery")) {
        setIsAddressModalOpen(true);
      }
    } catch (error) {}
  };
  return {
      // data
      normalizedItems,
      cartItems,
      // actions
      isAllSelected,
      loading,
      actionLoading,

      //functions
      toggleSelectAll,
      toggleSelect,
      navigate,
      updateQuantity,
      updateCartItemQuantity,
      handleRemove,
      handleCartItemClick,
      subTotal,
      total,
      selectedItems,
      onCheckout,
      isAddressModalOpen,
      toggleAddressModal,
  };
};
